import React, { Component } from "react";
import { FormattedMessage, type IntlShape, injectIntl } from "react-intl";
import { type ConnectedProps, connect } from "react-redux";
import type { RouteComponentProps } from "react-router-dom";
import { isDirty } from "redux-form";

import { eraseStateProp } from "~/actions/dashboard";
import { getToken } from "~/actions/get_token";
import { createAndSubmitPatient, createPatient, PatientSubmitError, type TCreatePatientInstructions, updateAndSubmitPatient, updatePatient } from "~/actions/post_patient";
import { addUserActionNotificationError } from "~/actions/user_notification";
import { RxTypeId } from "~/common/constants";
import { isAdditionalAlignersCourse, isCapLikeCourse, isPowerCapCourse, isRetainersCourse } from "~/common/courses";
import { featureFlags } from "~/common/feature-flags";
import { convertToJSONFullRx, mergeCorrectionFormValues } from "~/common/instructions";
import { remoteLog } from "~/common/logging";
import { Material } from "~/common/prescription";
import { assertIsUser, canOrderTestPlastic, isUser } from "~/common/user";
import PowerCapFileUpload from "~/components/3d_plan_power_cap/power_cap_file_upload";
import { scrollTo } from "~/components/common/ScrollToElement/scrollTo";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { LoadingButton } from "~/components/ui/loading-button";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { setDocumentTitle } from "~/hooks/use-document-title";
import { selectClinics } from "~/slices/clinics";
import { getCourseInstallment, isCBCTIncludedInCourse, isCBCTServiceAvailableForCourse, selectCourseInstallment } from "~/slices/course-installment";
import { addInstructions, createInstructionsSuccess, selectInstructions, type TInstructions } from "~/slices/instructions";
import { selectAdditionalAlignersFormValues, selectCorrectionFormValues, selectLinksFormValues } from "~/slices/redux-form";
import { selectSmartPrescription } from "~/slices/smart_prescription";
import { selectUser } from "~/slices/user";
import type { RootState } from "~/store";

import type { ReduxFormLinks } from "./addLinks";
import { PatientNewAdditionalAligners } from "./patient_new/patient_new_additional_aligners";
import {
  type AdditionalPaidServiceTag,
  isSmartPrescriptionFillingSelected,
  PatientNewAdditionalServices,
  resolveDeepCBCTChecked,
  resolveShowDeepCBCT,
} from "./patient_new/patient_new_additional_services";
import { PatientNewAudioNote } from "./patient_new/patient_new_audio_note";
import { PatientNewCourse } from "./patient_new/patient_new_course";
import { DiagnosisTextArea } from "./patient_new/patient_new_diagnosis";
import { PatientNewDoctorInfo } from "./patient_new/patient_new_doctor_info";
import { PatientNewFiles } from "./patient_new/patient_new_files";
import { PatientNewImages } from "./patient_new/patient_new_images";
import PatientNewPatientInfo from "./patient_new/patient_new_patient_info";
import PatientNewPayment from "./patient_new/patient_new_payment";
import { type TPatientNewRequiredField, validateReqiredFields, validateRequiredDraftFields } from "./patient_new_validation";
import { MaterialOptions } from "./patient_shared/material_options";
import { SmartPrescriptionCoordinator } from "./patient_shared/smart_prescription_coordinator";
import { TestPlaticOptions } from "./patient_shared/test_plastic_options";
import PatientUpdateUploadCT from "./patient_update/patient_update_upload_ct";
import RecipeForm from "./recipe_redux_form";
import RouteLeavingGuard from "./route_leaving_guard";

function flattenLinkValues(links: ReduxFormLinks | undefined): string[] {
  const linksArray = links?.links ?? [];
  return linksArray.flatMap((link) => (link != null ? Object.values(link) : []));
}

export const mapStateToProps = (state: RootState) => {
  return {
    user: selectUser(state),
    media: state.media,
    media_s3: state.media_s3,
    instructions: selectInstructions(state),
    smartPrescription: selectSmartPrescription(state),
    clinics: selectClinics(state),
    formValues: selectCorrectionFormValues(state),
    areFormValuesDirty: isDirty("correction")(state),
    additionalAlignersFormValues: selectAdditionalAlignersFormValues(state),
    links: selectLinksFormValues(state),
    areLinksDirty: isDirty("links")(state),
    courseInstallment: selectCourseInstallment(state),
  };
};

export const mapDispatchToProps = {
  createAndSubmitPatient,
  createPatient,
  updateAndSubmitPatient,
  updatePatient,
  initInstrucrions: createInstructionsSuccess,
  getToken,
  eraseStateProp,
  getCourseInstallment,
  addInstructions,
  addUserActionNotificationError,
};

// `bareLayout` (own prop): render without the legacy page chrome so the form can be embedded in the
// redesign-v1 shell. Optional and default-off, so legacy callers are unchanged.
export type PatientNewProps = PropsFromRedux & {
  intl: IntlShape;
  bareLayout?: boolean;
} & RouteComponentProps;

type PatientNewState = {
  isSubmitPending: boolean;
  isDraftPending: boolean;
  isDirty: boolean;
  currentPath: string | null;
  submit: boolean;
};

export class PatientNew extends Component<PatientNewProps, PatientNewState> {
  /** Registered by the audio widget so the recipe / modal buttons can start recording. */
  protected recordTriggerRef: React.MutableRefObject<(() => void) | null> = { current: null };

  /** Draft that was created but not submitted. The next attempt updates it instead of creating another patient. */
  private createdPatientId: number | null = null;

  constructor(props: PatientNewProps) {
    super(props);
    this.state = {
      isSubmitPending: false,
      isDraftPending: false,
      isDirty: false,
      currentPath: null,
      submit: false,
    };
    this.submitButton = this.submitButton.bind(this);
    this.submitSaveButton = this.submitSaveButton.bind(this);
    this.setDirty = this.setDirty.bind(this);
    this.selectAdditionalServiceTags = this.selectAdditionalServiceTags.bind(this);
    this.handleRecordAudio = this.handleRecordAudio.bind(this);
  }

  /** Scroll to the audio widget and start recording (recipe / result-modal buttons). */
  protected handleRecordAudio() {
    scrollTo({ id: "smart-audio-note", duration: 600 });
    this.recordTriggerRef.current?.();
  }

  protected scrollToField(id: string) {
    scrollTo({ id, duration: 1500 });
  }

  async submitButton() {
    assertIsUser(this.props.user);
    this.setState({ submit: true });

    const { user, instructions, additionalAlignersFormValues } = this.props;
    const formValues = this.props.formValues;
    const rxTypeId = RxTypeId.STANDARD;
    const emptyFields: TPatientNewRequiredField[] = [];
    const links = flattenLinkValues(this.props.links);

    const instructionsCopy: TCreatePatientInstructions = { ...instructions };

    if (this.props.clinics.length == 1 && !instructions.clinic_id) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      instructionsCopy.clinic_id = Number(this.props.clinics[0]!.clinic_id);
    }

    const areRequiredFieldsValid = validateReqiredFields({
      user,
      links,
      instructions,
      formValues,
      additionalAlignersFormValues,
      media: this.props.media,
      emptyFields,
    });

    if (!areRequiredFieldsValid) {
      this.props.addUserActionNotificationError({ message: "notify.patient.required_fields" });
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.scrollToField(emptyFields[0]!);
      return;
    }

    // Смарт-услуга: рецепт заполняется grok'ом в форме — сохраняем значения
    // формы, а не пустой рецепт (иначе заполненное теряется при сохранении). Мерджим
    // дефолты рецепта под значения формы: поля, для которых нет инпутов в форме
    // (напр. correct_proclination_*), обязательны на бэке — без них create/update
    // отдаёт 400. Значения формы (в т.ч. grok) перекрывают.
    instructionsCopy.prescription = convertToJSONFullRx(mergeCorrectionFormValues(formValues));
    instructionsCopy.prescription.rx_type_id = rxTypeId;
    instructionsCopy.links = links;
    instructionsCopy.additional_aligners = additionalAlignersFormValues?.aligners;

    this.setState({ isSubmitPending: true });

    try {
      if (this.createdPatientId !== null) {
        await this.props.updateAndSubmitPatient(this.createdPatientId, {
          instructions: instructionsCopy,
          replaceLinks: true,
          media: this.props.media,
          media_s3: this.props.media_s3,
        });
      } else {
        await this.props.createAndSubmitPatient(instructionsCopy, this.props.media, this.props.media_s3);
      }
      this.props.history.push("/pages/patients");
    } catch (err) {
      if (err instanceof PatientSubmitError) {
        this.createdPatientId = err.patientId;
      }
      this.setState({ isSubmitPending: false });
    }
  }

  async submitSaveButton() {
    assertIsUser(this.props.user);
    this.setState({ submit: true });

    const { instructions, formValues } = this.props;
    const rxTypeId = RxTypeId.STANDARD;
    const emptyFields: TPatientNewRequiredField[] = [];
    const links = flattenLinkValues(this.props.links);

    // Validate before converting prescription — missing FIO (etc.) should scroll, not crash.
    const areRequiredFieldsValid = validateRequiredDraftFields({
      links,
      instructions,
      formValues,
      emptyFields,
    });

    if (!areRequiredFieldsValid) {
      this.props.addUserActionNotificationError({ message: "notify.patient.required_fields" });
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.scrollToField(emptyFields[0]!);
      return;
    }

    const instructionsCopy: TCreatePatientInstructions = { ...instructions };

    // Смарт-услуга: рецепт заполняется grok'ом в форме — сохраняем значения
    // формы, а не пустой рецепт (иначе заполненное теряется при сохранении). Мерджим
    // дефолты рецепта под значения формы: поля, для которых нет инпутов в форме
    // (напр. correct_proclination_*), обязательны на бэке — без них create/update
    // отдаёт 400. Значения формы (в т.ч. grok) перекрывают.
    instructionsCopy.prescription = convertToJSONFullRx(mergeCorrectionFormValues(formValues));
    instructionsCopy.prescription.rx_type_id = rxTypeId;
    instructionsCopy.links = links;

    this.setState({ isDraftPending: true, isDirty: false });

    try {
      if (this.createdPatientId !== null) {
        await this.props.updatePatient(this.createdPatientId, {
          instructions: instructionsCopy,
          replaceLinks: true,
          media: this.props.media,
          media_s3: this.props.media_s3,
        });
      } else {
        await this.props.createPatient(instructionsCopy, this.props.media, this.props.media_s3);
      }
      this.props.history.push("/pages/patients");
    } catch {
      this.setState({ isDraftPending: false });
    }
  }

  componentDidMount() {
    this.props.initInstrucrions();

    void this.props.getToken();
    void this.props.getCourseInstallment();

    this.props.eraseStateProp("patient");
    this.props.eraseStateProp("instructions");
    this.props.eraseStateProp("media");

    this.setState({ currentPath: window.location.pathname });

    setDocumentTitle(this.props.intl.formatMessage({ id: "left.menu.add_patient" }));
  }

  componentDidUpdate(_prevProps: PatientNewProps) {
    const { submit } = this.state;
    const formIsDirty = !submit && this.getIsFormDirty();

    if (formIsDirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }

    if (this.props.instructions.audio_note != null && !isSmartPrescriptionFillingSelected(this.props.instructions.additional_service_tags)) {
      this.props.addInstructions({ audio_note: null });
    }
  }

  componentWillUnmount() {
    window.onbeforeunload = null;
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "patient_new");
  }

  setDirty<T extends keyof TInstructions>(key: T, value: TInstructions[T]) {
    const { instructions } = this.props;
    this.setState({ isDirty: value !== "" || instructions[key] !== "" });
  }

  getIsFormDirty(): boolean {
    const { media, media_s3, areFormValuesDirty, areLinksDirty } = this.props;
    const { isDirty } = this.state;

    const isMediaDirty = Object.keys(media).length > 0;
    const isS3MediaDirty = Array.isArray(media_s3.files) && media_s3.files.length > 0;

    return isDirty || areFormValuesDirty || areLinksDirty || isMediaDirty || isS3MediaDirty;
  }

  selectDeepCBCTAnalysis(value: boolean) {
    this.props.addInstructions({ deep_cbct: value });
    this.setDirty("deep_cbct", value);
  }

  addCondition(data: string) {
    this.props.addInstructions({ condition: data });
    this.setDirty("condition", data);
  }

  selectAdditionalServiceTags(tags: AdditionalPaidServiceTag[]) {
    this.props.addInstructions({ additional_service_tags: tags });
    this.setDirty("additional_service_tags", tags);
  }

  render() {
    const {
      user,
      courseInstallment,
      instructions: { course_id, deep_cbct, test_plastic, additional_service_tags = [], audio_note = null, clinic_id },
      formValues: { material } = {},
    } = this.props;

    const { currentPath, submit } = this.state;

    if (!isUser(user) || !courseInstallment) {
      // NOTE: when you are on new patient page and press logout, this page rerenders without a user.
      return (
        <Layout chrome={!this.props.bareLayout}>
          <Loader />
        </Layout>
      );
    }

    const formIsDirty = !submit && this.getIsFormDirty();
    const hasSmartPrescriptionFilling = isSmartPrescriptionFillingSelected(additional_service_tags);
    // Смарт-рецепт: рецепт доступен для ручного заполнения всегда; блокируем его
    // только на время обработки аудио моделью (чтобы grok не перезаписал правки).
    const { isProcessing: smartRxProcessing } = this.props.smartPrescription;
    const recipeDisabled = hasSmartPrescriptionFilling && smartRxProcessing;
    // Deep CBCT analysis and other paid add-ons do not apply to power-cap orders.
    const additionalServicesSection =
      course_id == null || isPowerCapCourse(course_id) ? null : (
        <PatientNewAdditionalServices
          courseId={course_id}
          showDeepCBCT={Boolean(courseInstallment[course_id]) && !isCBCTIncludedInCourse(courseInstallment, course_id) && resolveShowDeepCBCT(courseInstallment[course_id]?.deep_cbct != null)}
          deepCBCTDisabled={!courseInstallment[course_id] || !isCBCTServiceAvailableForCourse(courseInstallment, course_id)}
          deepCBCTChecked={resolveDeepCBCTChecked(deep_cbct == true, Boolean(courseInstallment[course_id]) && isCBCTIncludedInCourse(courseInstallment, course_id))}
          onDeepCBCTCheckedChange={(checked) => this.selectDeepCBCTAnalysis(checked)}
          additionalServiceTags={additional_service_tags}
          onAdditionalServiceTagsChange={this.selectAdditionalServiceTags}
        />
      );

    // Power-cap price depends on the selected arch, so its recipe fields stay next to the price.
    const recipeSection = <RecipeForm course_id={course_id} disabled={recipeDisabled} commentLabelId={isPowerCapCourse(course_id) ? "pat.power_cap.comment" : undefined} />;

    return (
      <Layout chrome={!this.props.bareLayout}>
        <Portlet as="main" testId="new-patient-page">
          <PortletTitle iconClassName="icon-book-open" displayRequiredFieldsInfo>
            <FormattedMessage id="left.menu.add_patient" />
          </PortletTitle>

          {/* NOTE: Because we can't save additional aligners course as draft. */}
          {isAdditionalAlignersCourse(course_id) ? null : (
            <RouteLeavingGuard when={formIsDirty} shouldBlockNavigation={() => formIsDirty} saveChanges={this.submitSaveButton} saveAsADraft={true} currentPath={currentPath} />
          )}

          <div className="portlet-body form">
            <div className="form-body">
              <PatientNewDoctorInfo user={user} setDirty={this.setDirty} />
              <PatientNewPatientInfo setDirty={this.setDirty} />
              <PatientNewCourse
                courseInstallment={courseInstallment}
                setDirty={this.setDirty}
                // Flag on: additional services belong below the selected course. Flag off:
                // 36.2.12: отдельная `.form-group` ПОСЛЕ «Оплаты», см. строку ниже.
                additionalServices={featureFlags.additionalPaidServices ? additionalServicesSection : undefined}
              />
              {isPowerCapCourse(course_id) ? recipeSection : null}
              <PatientNewPayment courseInstallment={courseInstallment} setDirty={this.setDirty} />
              {featureFlags.additionalPaidServices ? null : additionalServicesSection}
              {isCapLikeCourse(course_id) || isAdditionalAlignersCourse(course_id) ? null : <DiagnosisTextArea onValueChange={(value) => this.addCondition(value)} />}
              {isAdditionalAlignersCourse(course_id) || hasSmartPrescriptionFilling || isPowerCapCourse(course_id) ? null : <MaterialOptions />}
              {canOrderTestPlastic(user) ? <TestPlaticOptions value={test_plastic} onValueChange={(test_plastic) => this.props.addInstructions({ test_plastic })} /> : null}
              {hasSmartPrescriptionFilling ? (
                <>
                  <PatientNewAudioNote
                    audioNote={audio_note}
                    clinicId={clinic_id}
                    recordTriggerRef={this.recordTriggerRef}
                    onChange={(nextAudioNote) => {
                      this.props.addInstructions({ audio_note: nextAudioNote });
                      this.setDirty("audio_note", nextAudioNote);
                    }}
                  />
                  <SmartPrescriptionCoordinator onRecordAudio={this.handleRecordAudio} />
                  <PatientNewFiles isVisible smartPrescriptionMode />
                </>
              ) : null}
              {isAdditionalAlignersCourse(course_id) || hasSmartPrescriptionFilling ? null : isPowerCapCourse(course_id) ? (
                <PowerCapFileUpload required isVisible />
              ) : (
                <PatientNewFiles required={isCapLikeCourse(course_id) ? material == Material.SCAN : false} isVisible={isCapLikeCourse(course_id) ? material == Material.SCAN : true} />
              )}
              {isAdditionalAlignersCourse(course_id) || hasSmartPrescriptionFilling || isPowerCapCourse(course_id) ? null : isRetainersCourse(course_id) ? null : (
                <>
                  <PatientUpdateUploadCT />
                  <PatientNewImages />
                </>
              )}
              {isAdditionalAlignersCourse(course_id) ? <PatientNewAdditionalAligners /> : null}
              {isPowerCapCourse(course_id) ? null : recipeSection}
            </div>

            <div className="tw-flex tw-flex-wrap tw-gap-3 tw-border-t tw-border-[#e7ecf1] tw-py-5 sm:tw-gap-2">
              <LoadingButton
                id="submit-pacient-btn"
                data-testid="new-patient-submit"
                type="button"
                variant="primary"
                size="xl"
                className="tw-w-full sm:tw-max-w-[180px]"
                disabled={this.state.isDraftPending}
                onClick={() => this.submitButton()}
                isLoading={this.state.isSubmitPending}
              >
                <FormattedMessage id="BTN_SUBMIT_NEW" />
              </LoadingButton>

              {isAdditionalAlignersCourse(course_id) ? null : (
                <LoadingButton
                  id="save-pacient-btn"
                  data-testid="new-patient-save-draft"
                  type="button"
                  variant="secondary"
                  size="xl"
                  className="tw-w-full sm:tw-max-w-[180px]"
                  disabled={this.state.isSubmitPending}
                  onClick={() => this.submitSaveButton()}
                  isLoading={this.state.isDraftPending}
                >
                  <FormattedMessage id="BTN_SAVE_DRAFT" />
                </LoadingButton>
              )}

              {hasSmartPrescriptionFilling ? (
                <LoadingButton
                  id="record-audio-btn"
                  data-testid="new-patient-record-audio"
                  type="button"
                  variant="outline"
                  size="xl"
                  className="tw-w-full sm:tw-max-w-[180px]"
                  disabled={this.state.isSubmitPending || this.state.isDraftPending}
                  onClick={this.handleRecordAudio}
                  isLoading={false}
                >
                  Записать аудио
                </LoadingButton>
              ) : null}
            </div>
          </div>
        </Portlet>
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PatientNew));
